<template>
  <section class="waajo-kanban-column" :class="'is-' + column.color">
    <header class="waajo-kanban-column-head">
      <span class="waajo-kanban-column-icon">
        <i :class="column.icon"></i>
      </span>
      <div class="waajo-kanban-column-title">
        <h4>{{ column.label }}</h4>
        <small>{{ column.description }}</small>
      </div>
      <span class="waajo-kanban-column-count">{{ column.total }}</span>
    </header>

    <div v-if="column.loading && !column.items.length" class="waajo-kanban-column-loading">
      <div v-for="n in 3" :key="'kanban-sk-' + n" class="waajo-kanban-card-skeleton">
        <span class="waajo-kanban-sk is-number"></span>
        <span class="waajo-kanban-sk"></span>
        <span class="waajo-kanban-sk is-short"></span>
      </div>
    </div>

    <div v-else-if="column.error && !column.items.length" class="waajo-kanban-column-state is-error">
      <i class="fa fa-exclamation-circle"></i>
      <p>{{ column.error }}</p>
      <button type="button" @click="$emit('retry', column.status)">Coba lagi</button>
    </div>

    <template v-else>
      <div v-if="column.error" class="waajo-kanban-column-state is-inline-error">
        <i class="fa fa-exclamation-circle"></i>
        <span>{{ column.error }}</span>
        <button type="button" @click="$emit('retry', column.status)">Coba lagi</button>
      </div>

      <p v-if="column.refreshing" class="waajo-kanban-column-refreshing">
        <i class="fa fa-spinner fa-spin"></i> Memperbarui...
      </p>

      <draggable
        v-model="column.items"
        class="waajo-kanban-column-list"
        :data-status="column.status"
        group="waajo-ticket-kanban"
        :animation="170"
        handle=".ticket-card-drag-area"
        ghost-class="waajo-kanban-card-ghost"
        chosen-class="waajo-kanban-card-chosen"
        drag-class="waajo-kanban-card-drag"
        :disabled="dragDisabled"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <ticket-kanban-card
          v-for="ticket in column.items"
          :key="ticket.id"
          :ticket="ticket"
          :busy="String(movingId) === String(ticket.id)"
          :drag-disabled="dragDisabled"
          @open="$emit('open', $event)"
          @move="$emit('move', $event)"
        />
      </draggable>

      <div v-if="!column.items.length && !column.loading" class="waajo-kanban-column-empty">
        <i class="fa fa-inbox"></i>
        <p>{{ emptyMessage }}</p>
      </div>

      <div class="waajo-kanban-column-foot">
        <span class="waajo-kanban-column-progress" :class="{ 'is-empty': !column.total }">
          Menampilkan {{ column.items.length }} dari {{ column.total }} tiket
        </span>
        <button
          v-if="column.hasMore"
          type="button"
          class="waajo-kanban-load-more"
          :disabled="column.loadingMore || dragDisabled"
          @click="$emit('load-more', column.status)"
        >
          <i v-if="column.loadingMore" class="fa fa-spinner fa-spin"></i>
          {{ column.loadingMore ? 'Memuat...' : 'Load More' }}
        </button>
        <span v-else-if="column.total > 0" class="waajo-kanban-column-complete">
          <i class="fa fa-check"></i> Semua tiket dimuat
        </span>
      </div>
    </template>
  </section>
</template>

<script>
import draggable from 'vuedraggable'
import TicketKanbanCard from './TicketKanbanCard.vue'

export default {
  name: 'TicketKanbanColumn',
  components: {
    draggable,
    TicketKanbanCard
  },
  props: {
    column: {
      type: Object,
      required: true
    },
    dragDisabled: {
      type: Boolean,
      default: false
    },
    movingId: {
      type: [String, Number],
      default: null
    },
    filterActive: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    emptyMessage() {
      return this.filterActive
        ? 'Tidak ada tiket yang cocok dengan filter ini.'
        : 'Belum ada tiket pada status ini.'
    }
  },
  methods: {
    onDragStart(event) {
      this.$emit('drag-start', this.column.status, event)
    },
    onDragEnd(event) {
      this.$emit('drag-end', this.column.status, event)
    }
  }
}
</script>

<style scoped>
.waajo-kanban-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: calc(100vh - 250px);
  border: 1px solid var(--waajo-border);
  border-radius: 14px;
  background: #f8fafc;
  overflow: hidden;
}
.waajo-kanban-column-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
  border-bottom: 1px solid var(--waajo-border);
  background: #fff;
}
.waajo-kanban-column-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 15px;
}
.waajo-kanban-column.is-open .waajo-kanban-column-icon {
  background: var(--waajo-brand-soft);
  border: 1px solid var(--waajo-brand-border);
  color: var(--waajo-brand-ink);
}
.waajo-kanban-column.is-progress .waajo-kanban-column-icon {
  background: var(--waajo-status-progress-bg);
  border: 1px solid var(--waajo-status-progress-border);
  color: var(--waajo-status-progress-ink);
}
.waajo-kanban-column.is-resolved .waajo-kanban-column-icon {
  background: var(--waajo-status-resolved-bg);
  border: 1px solid var(--waajo-status-resolved-border);
  color: var(--waajo-status-resolved-ink);
}
.waajo-kanban-column-title {
  min-width: 0;
  flex: 1 1 auto;
}
.waajo-kanban-column-title h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--waajo-text);
  line-height: 1.3;
}
.waajo-kanban-column-title small {
  display: block;
  margin-top: 1px;
  font-size: 11px;
  color: var(--waajo-text-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.waajo-kanban-column-count {
  min-width: 30px;
  padding: 2px 9px;
  border-radius: 9999px;
  border: 1px solid var(--waajo-border);
  background: #f8fafc;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  color: var(--waajo-text-body);
}
.waajo-kanban-column.is-open .waajo-kanban-column-count {
  border-color: var(--waajo-brand-border);
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink);
}
.waajo-kanban-column.is-progress .waajo-kanban-column-count {
  border-color: var(--waajo-status-progress-border);
  background: var(--waajo-status-progress-bg);
  color: var(--waajo-status-progress-ink);
}
.waajo-kanban-column.is-resolved .waajo-kanban-column-count {
  border-color: var(--waajo-status-resolved-border);
  background: var(--waajo-status-resolved-bg);
  color: var(--waajo-status-resolved-ink);
}
.waajo-kanban-column-list {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  min-height: 120px;
  overflow-y: auto;
}
.waajo-kanban-column-loading {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
}
.waajo-kanban-card-skeleton {
  padding: 14px;
  border: 1px solid var(--waajo-border);
  border-radius: 12px;
  background: #fff;
}
.waajo-kanban-sk {
  display: block;
  height: 10px;
  width: 100%;
  margin-bottom: 8px;
  border-radius: 5px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: waajoKanbanSk 1.2s infinite;
}
.waajo-kanban-sk.is-number {
  width: 42%;
}
.waajo-kanban-sk.is-short {
  width: 64%;
  margin-bottom: 0;
}
@keyframes waajoKanbanSk {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.waajo-kanban-column-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  padding: 22px 14px;
  text-align: center;
  color: var(--waajo-text-muted);
  font-size: 12px;
  line-height: 1.5;
}
.waajo-kanban-column-state i {
  font-size: 22px;
  color: #cbd5e1;
}
.waajo-kanban-column-state p {
  margin: 0;
}
.waajo-kanban-column-state button {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
}
.waajo-kanban-column-state.is-error i {
  color: #fca5a5;
}
.waajo-kanban-column-state.is-error {
  color: #b42318;
}
.waajo-kanban-column-state.is-inline-error {
  flex-direction: row;
  justify-content: flex-start;
  gap: 6px;
  padding: 8px 12px;
  margin: 10px 12px 0;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  text-align: left;
}
.waajo-kanban-column-state.is-inline-error button {
  margin-left: auto;
  color: #b91c1c;
}
.waajo-kanban-column-refreshing {
  margin: 8px 12px 0;
  font-size: 11px;
  color: var(--waajo-text-muted);
}
.waajo-kanban-column-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 26px 14px;
  text-align: center;
  color: var(--waajo-text-faint);
  font-size: 12px;
}
.waajo-kanban-column-empty i {
  font-size: 26px;
  color: #d5dde8;
}
.waajo-kanban-column-empty p {
  margin: 0;
}
.waajo-kanban-column-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--waajo-border);
  background: #fff;
}
.waajo-kanban-column-progress {
  font-size: 11px;
  color: var(--waajo-text-muted);
}
.waajo-kanban-column-progress.is-empty {
  color: var(--waajo-text-faint);
}
.waajo-kanban-column-complete {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: var(--waajo-status-resolved-ink);
}
.waajo-kanban-load-more {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  padding: 0 11px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 9999px;
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink);
  font-size: 11px;
  font-weight: 700;
}
.waajo-kanban-load-more:hover,
.waajo-kanban-load-more:focus {
  border-color: var(--waajo-brand-border-strong);
  background: var(--waajo-brand-soft);
}
.waajo-kanban-load-more:disabled {
  opacity: 0.6;
}
.waajo-kanban-card-ghost {
  opacity: 0.45;
  background: #fff7e8 !important;
  border-style: dashed !important;
}
.waajo-kanban-card-chosen {
  cursor: grabbing;
}
.waajo-kanban-card-drag {
  transform: rotate(1.5deg);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.2);
}
@media (max-width: 1100px) {
  .waajo-kanban-column {
    max-height: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketKanbanColumn.vue